import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, Lock, Search } from 'lucide-react';
import UnsavedChangesDialog, { useUnsavedChanges } from '../components/UnsavedChangesDialog';

export default function VeiculosPage() {
  const { canManageCadastros, canOnlyView } = useAuth();
  const canEdit = canManageCadastros();
  
  const [veiculos, setVeiculos] = useState([]);
  const [secretarias, setSecretarias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    code: '',
    plate: '',
    description: '',
    secretaria_id: '',
    initial_km: 0,
    status: 'active'
  });
  
  // Hook para controle de alterações não salvas
  const unsavedChanges = useUnsavedChanges();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [veiculosRes, secretariasRes] = await Promise.all([
        api.get('/veiculos'),
        api.get('/secretarias')
      ]);
      setVeiculos(veiculosRes.data);
      setSecretarias(secretariasRes.data);
    } catch (error) {
      toast.error('Erro ao carregar veículos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/veiculos/${editingId}`, formData);
        toast.success('Veículo atualizado com sucesso!');
      } else {
        await api.post('/veiculos', formData);
        toast.success('Veículo cadastrado com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar veículo');
    }
  };

  const handleEdit = (veiculo) => {
    setFormData(veiculo);
    setEditingId(veiculo.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este veículo?')) return;
    try {
      await api.delete(`/veiculos/${id}`);
      toast.success('Veículo excluído com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir veículo');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      plate: '',
      description: '',
      secretaria_id: '',
      initial_km: 0,
      status: 'active'
    });
    setEditingId(null);
    unsavedChanges.resetChanges();
  };

  // Função para lidar com mudanças no formulário
  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    unsavedChanges.markAsChanged();
  };

  // Função para tentar fechar o dialog
  const handleDialogClose = (open) => {
    if (!open && unsavedChanges.hasChanges) {
      unsavedChanges.tryAction(() => {
        setDialogOpen(false);
        resetForm();
      });
    } else {
      setDialogOpen(open);
      if (!open) resetForm();
    }
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div></div>;
  }

  const filteredVeiculos = veiculos
    .filter(v => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (v.plate || '').toLowerCase().includes(q) || (v.description || '').toLowerCase().includes(q) || (v.code || '').toLowerCase().includes(q);
    })
    .sort((a, b) => (a.plate || '').localeCompare(b.plate || '', 'pt-BR'));

  return (
    <div className="space-y-6" data-testid="veiculos-page">
      {/* Diálogo de confirmação para sair sem salvar */}
      <UnsavedChangesDialog
        isOpen={unsavedChanges.showDialog}
        onOpenChange={unsavedChanges.setShowDialog}
        onConfirmLeave={unsavedChanges.confirmLeave}
      />
      
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-[#003366]" style={{ fontFamily: 'Manrope, sans-serif' }}>Veículos</h2>
          <p className="text-slate-600">Gerenciamento da frota municipal</p>
        </div>
        {canEdit ? (
          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button onClick={openNewDialog} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="add-veiculo-button">
                <Plus className="mr-2" size={20} />
                Novo Veículo
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Editar Veículo' : 'Novo Veículo'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="veiculo-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Código Interno *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    required
                    data-testid="veiculo-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="plate">Placa *</Label>
                  <Input
                    id="plate"
                    value={formData.plate}
                    onChange={(e) => setFormData({ ...formData, plate: e.target.value.toUpperCase() })}
                    required
                    data-testid="veiculo-plate-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descrição/Modelo *</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="veiculo-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="secretaria">Secretaria</Label>
                  <select
                    id="secretaria"
                    value={formData.secretaria_id}
                    onChange={(e) => setFormData({ ...formData, secretaria_id: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="veiculo-secretaria-select"
                  >
                    <option value="">Selecione...</option>
                    {secretarias.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="initial_km">Quilometragem Inicial</Label>
                  <Input
                    id="initial_km"
                    type="number"
                    step="0.01"
                    value={formData.initial_km}
                    onChange={(e) => setFormData({ ...formData, initial_km: parseFloat(e.target.value) })}
                    data-testid="veiculo-km-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status *</Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="veiculo-status-select"
                >
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </select>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" className="bg-[#003366] hover:bg-[#002244]" data-testid="veiculo-submit-button">Salvar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        ) : (
          <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-lg">
            <Lock size={18} />
            <span className="text-sm font-medium">Modo Visualização</span>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Lista de Veículos</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <Input placeholder="Buscar por placa, modelo..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="search-veiculos" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Placa</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Km Inicial</TableHead>
                <TableHead>Status</TableHead>
                {canEdit && <TableHead className="text-right">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredVeiculos.map((veiculo) => (
                <TableRow key={veiculo.id} className="table-row" data-testid={`veiculo-row-${veiculo.id}`}>
                  <TableCell className="font-medium">{veiculo.code}</TableCell>
                  <TableCell>{veiculo.plate}</TableCell>
                  <TableCell>{veiculo.description}</TableCell>
                  <TableCell>{veiculo.initial_km} km</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      veiculo.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {veiculo.status === 'active' ? 'Ativo' : 'Inativo'}
                    </span>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(veiculo)}
                          data-testid={`edit-veiculo-${veiculo.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(veiculo.id)}
                          data-testid={`delete-veiculo-${veiculo.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {filteredVeiculos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 6 : 5} className="text-center py-8 text-slate-500">
                    {searchQuery ? 'Nenhum resultado encontrado' : 'Nenhum veículo cadastrado'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}